// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatHumanName, normalizeErrorString } from '@medplum/core';
import type { HumanName, Practitioner, Task } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react';
import { IconCircleCheck, IconCircleOff, IconExternalLink } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import { getTaskAssociatedActionMeta } from '../../constants/task-associated-action';
import { withTenantScope } from '../../utils/tenant';
import classes from './TaskCard.module.css';

dayjs.extend(relativeTime);

const TERMINAL_STATUSES: readonly Task['status'][] = ['completed', 'cancelled', 'rejected', 'failed'];
const DESCRIPTION_TRUNCATE_LENGTH = 110;

type CardState = 'overdue' | 'urgent' | 'routine';

const STATE_LABELS: Record<CardState, string> = {
  overdue: 'Overdue',
  urgent: 'Urgent',
  routine: 'Routine',
};

export interface TaskCardProps {
  task: Task;
  /** `mine` shows action footer; `assigned` is read-only. */
  variant: 'mine' | 'assigned';
  onResolved?: (task: Task) => void;
  onEdit?: (task: Task) => void;
}

function getCardState(task: Task): CardState {
  const dueDateStr = task.restriction?.period?.end;
  const isTerminal = task.status && TERMINAL_STATUSES.includes(task.status);
  if (dueDateStr && !isTerminal && new Date(dueDateStr).getTime() < Date.now()) {
    return 'overdue';
  }
  if (task.priority === 'urgent' || task.priority === 'asap' || task.priority === 'stat') {
    return 'urgent';
  }
  return 'routine';
}

function getPractitionerName(p: Practitioner | undefined): string {
  if (!p?.name?.[0]) {
    return 'someone';
  }
  return formatHumanName(p.name[0] as HumanName);
}

export function TaskCard({ task, variant, onResolved, onEdit }: TaskCardProps): JSX.Element {
  const medplum = useMedplum();
  const requester = useResource(task.requester);
  const owner = useResource(task.owner);
  const patient = useResource(task.for);
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);

  const state = getCardState(task);
  const associatedAction = getTaskAssociatedActionMeta(task);

  const dueDateStr = task.restriction?.period?.end;
  const dueLabel = dueDateStr ? dayjs(dueDateStr).fromNow() : undefined;
  // dayjs `fromNow()` already renders both directions ("in 2 days" / "1 day ago"); use it directly.

  const stateLabel = STATE_LABELS[state];

  const requesterName =
    requester?.resourceType === 'Practitioner' ? getPractitionerName(requester as Practitioner) : 'someone';
  const ownerName = owner?.resourceType === 'Practitioner' ? getPractitionerName(owner as Practitioner) : 'someone';
  const patientName = patient?.resourceType === 'Patient' ? formatHumanName(patient.name?.[0] as HumanName) : undefined;
  const patientId = patient?.resourceType === 'Patient' ? patient.id : undefined;

  const title = task.code?.text ?? task.description ?? 'Untitled task';
  const description = task.description ?? '';
  const isLong = description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const visibleDescription =
    !isLong || expanded ? description : `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trimEnd()}...`;

  const handleResolve = async (): Promise<void> => {
    if (!task.id) {
      return;
    }
    setResolving(true);
    try {
      const updated = await medplum.updateResource<Task>(withTenantScope({ ...task, status: 'completed' }));
      notifications.show({ icon: <IconCircleCheck />, title: 'Resolved', message: 'Task marked as completed' });
      onResolved?.(updated);
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(error),
      });
    } finally {
      setResolving(false);
    }
  };

  const handleAssociatedAction = (): void => {
    const urls: Record<string, string> = {
      airview: 'https://airview.resmed.com/',
    };
    const url = associatedAction ? urls[associatedAction.code] : undefined;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Paper withBorder radius="md" className={classes.card}>
      <Group justify="space-between" align="center" px="sm" py={6} className={classes[`header_${state}`]}>
        <Text size="sm" c="inherit" fw={700} tt="capitalize">
          {stateLabel}
        </Text>
        {dueLabel && (
          <Text size="sm" c="inherit">
            due {dueLabel}
          </Text>
        )}
      </Group>

      <Stack gap={6} p="sm">
        <Text fw={700}>{title}</Text>

        <Box>
          {variant === 'mine' ? (
            <Text size="sm" c="dimmed">
              Assigned by{' '}
              <Text component="span" fw={500} c="dark">
                {requesterName}
              </Text>
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              Assigned to{' '}
              <Text component="span" fw={500} c="dark">
                {ownerName}
              </Text>
            </Text>
          )}
          {patientName && (
            <Text size="sm" c="dimmed">
              for{' '}
              {patientId ? (
                <Anchor component={Link} to={`/Patient/${patientId}`}>
                  {patientName}
                </Anchor>
              ) : (
                <Text component="span" fw={500} c="dark">
                  {patientName}
                </Text>
              )}
            </Text>
          )}
        </Box>

        {description && (
          <Text size="sm">
            {visibleDescription}
            {isLong && (
              <>
                {' '}
                <Anchor component="button" type="button" onClick={() => setExpanded((v) => !v)} size="sm">
                  {expanded ? 'show less' : 'show more'}
                </Anchor>
              </>
            )}
          </Text>
        )}

        {variant === 'mine' && (
          <Group gap="xs" mt={4}>
            {associatedAction ? (
              <>
                <Button size="xs" leftSection={<IconExternalLink size={14} />} onClick={handleAssociatedAction}>
                  {associatedAction.buttonLabel}
                </Button>
                <Button size="xs" variant="outline" onClick={handleResolve} loading={resolving}>
                  Resolve
                </Button>
                <Button size="xs" variant="outline" onClick={() => onEdit?.(task)}>
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button size="xs" onClick={handleResolve} loading={resolving}>
                  Resolve
                </Button>
                <Button size="xs" variant="outline" onClick={() => onEdit?.(task)}>
                  Edit
                </Button>
              </>
            )}
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
